
import { Component, Vue } from 'nuxt-property-decorator'
import type { MetaInfo } from 'vue-meta'
import { Context } from '@nuxt/types'
import { createAmpOfferInstance, HomeOffer, MobileOffer } from '@alao-frontend/core'
import BlackFridayHeader from '~/components/black-friday/BlackFridayHeader.vue'
import BlackFridayEmailForm from '~/components/black-friday/BlackFridayEmailForm.vue'
import BlackFridayOfferSlider from '~/components/black-friday/BlackFridayOfferSlider.vue'
import Partners from '~/components/black-friday/Partners.vue'
import BlackFridayHiw from '~/components/black-friday/BlackFridayHiw.vue'
import BlackFridayFeatureBanner from '~/components/black-friday/BlackFridayFeatureBanner.vue'
import BlackFridayDealsSection from '~/components/black-friday/BlackFridayDealsSection.vue'
import BlackFridayMoreDeals from '~/components/black-friday/BlackFridayMoreDeals.vue'
import Footer from '~/components/promos/black-promo/BPFooter.vue'
import BlackFridayClientFeedback from '~/components/black-friday/BlackFridayClientFeedback.vue'
import SaveRouteMixin from '~/mixins/save-route.mixin'
import { OnMounted, WithAsyncData, WithHead } from '~/core/vue.types'
import { $api } from '~/plugins/axios'
import { NotifiesOfferType } from '~/services/notifier.service'
import { CompareOfferDTO } from '~/services/offer.service'
import { Provider } from '~/core/offer.types'
import { namespace as offerStore } from '~/store/modules/Offer'
import ProviderCommonCard from '~/components/providers/ProviderCommonCard.vue'
import BlackFridayPageBody from '~/components/black-friday/BlackFridayPageBody.vue'
import { useSEOMetaComposer } from '~/composables/seo.composable'
import BPSection from '~/components/promos/black-promo/snippets/BPSection.vue'
import BPPlans from '~/components/promos/black-promo/BPDeals/BPPlans.vue'
import BFFaq from '~/components/black-friday/BFFaq.vue'

const AVAILABLE_PROVIDERS = ['swisscom', 'salt', 'wingo', 'yallo', 'migros-mobile', 'quickline', 'talktalk', 'spusu', 'mtel']

@Component({
  components: {
    BlackFridayPageBody,
    BlackFridayHeader,
    BlackFridayEmailForm,
    BlackFridayOfferSlider,
    Partners,
    BlackFridayHiw,
    BlackFridayFeatureBanner,
    BlackFridayDealsSection,
    BlackFridayMoreDeals,
    Footer,
    BlackFridayClientFeedback,
    ProviderCommonCard,
    BPSection,
    BPPlans,
    BFFaq,
  },
  mixins: [SaveRouteMixin],
})
export default class BlackFridayProviderPage
  extends Vue
  implements OnMounted, WithHead, WithAsyncData {
  private isOffersLoading = true
  readonly provider!: Provider
  mobileOffers: MobileOffer[] = []
  homeOffers: HomeOffer[] = []
  private offerType = NotifiesOfferType.MOBILE_OFFER

  head (): MetaInfo {
    const slug = this.provider.slug
    const titleKey = `head.meta.blackFriday.providers.${slug}.title`
    const descriptionKey = `head.meta.blackFriday.providers.${slug}.description`

    return useSEOMetaComposer(
      this,
      {
        title: this.$te(titleKey)
          ? this.$t(titleKey).toString()
          : this.$t('head.meta.blackFriday.providers.title', {
            name: this.provider.name,
          }).toString(),
        description: this.$te(descriptionKey)
          ? this.$t(descriptionKey).toString()
          : this.$t('head.meta.blackFriday.providers.description', {
            name: this.provider.name,
          }).toString(),
        route: { name: 'black-friday-slug', params: { slug: this.$route.params.slug } },
      },
    )
  }

  asyncData ({ params, store, redirect, localePath }: Context) {
    const slug: string = params.slug

    if (!AVAILABLE_PROVIDERS.includes(slug)) {
      redirect(localePath('black-friday'))
      return
    }

    return {
      provider: store.getters[`${offerStore}/getProviderBySlug`](slug),
    }
  }

  get moreDealsLinks (): { text: string, url: string }[] {
    const mobileProviders = ['mobile-plans', 'swisscom', 'salt', 'wingo', 'yallo']

    return [
      {
        text: this.$t('blackFriday.moreDealsBanner.mobilePlans') as string,
        url: this.localePath('black-friday-mobile-plans'),
      },
      ...mobileProviders
        .map((slug) => {
          const provider = this.$store.getters[`${offerStore}/getProviderBySlug`](slug)
          if (!provider) {
            return null
          }
          return {
            text: this.$t('blackFriday.providers.link', {
              name: provider.name,
            }) as string,
            url: this.localePath({
              name: 'black-friday-slug',
              params: {
                slug: provider.slug,
              },
            }),
          }
        })
        .filter((item): item is { text: string, url: string } => item !== null),
    ]
  }

  get otherLinks (): { text: string, url: string }[] {
    return [
      {
        text: this.$t('blackFriday.moreDealsBanner.tips') as string,
        url: this.localePath({
          name: 'blogs-slug',
          params: {
            slug: 'black-friday-tips',
          },
        }),
      },
      {
        text: this.$t('blackFriday.moreDealsBanner.facts') as string,
        url: this.localePath({
          name: 'blogs-slug',
          params: {
            slug: 'black-friday-facts',
          },
        }),
      },
    ]
  }

  get otherProviderDealsLinks (): { text: string, url: string }[] {
    const internetProviders = ['migros-mobile', 'quickline', 'spusu', 'mtel', 'talktalk']
    return internetProviders
      .map((slug) => {
        const provider = this.$store.getters[`${offerStore}/getProviderBySlug`](slug)
        if (!provider) {
          return null
        }
        return {
          text: this.$t('blackFriday.providers.link', {
            name: provider.name,
          }) as string,
          url: this.localePath({
            name: 'black-friday-slug',
            params: {
              slug: provider.slug,
            },
          }),
        }
      })
      .filter((item): item is { text: string, url: string } => item !== null)
  }

  async fetchOffers (): Promise<void> {
    try {
      const [mobileOffers, homeOffers] = await Promise.all([
        $api.PublicOfferService.searchOffers({
          limit: 3,
          mobile_provider_slugs: [this.provider.slug],
          product_type: 'mobile',
        }),
        $api.PublicOfferService.searchOffers({
          limit: 3,
          home_provider_slugs: [this.provider.slug],
          product_type: 'home',
        }),
      ])

      this.mobileOffers = mobileOffers.items.map(item => createAmpOfferInstance(item))
      this.homeOffers = homeOffers.items.map(item => createAmpOfferInstance(item))
      this.isOffersLoading = false
    } catch (error:any) {
      this.$notify({
        title: 'Error',
        message: error.message,
        duration: 3000,
      })
    }
  }

  mounted (): void {
    this.$analytics.GTM.sendPageViewEvent({
      routeName: this.$route.name as string,
      pageTitle: this.$metaInfo.title || '',
      fullPath: this.$route.fullPath,
      fromPath: this.$nuxt.context.from ? this.$nuxt.context.from.fullPath : '',
      contentGroup: 'Black Friday page',
    })
    this.fetchOffers()
  }

  excludeOffers<T extends MobileOffer[] | HomeOffer[]>(
    offerResponse: CompareOfferDTO<T>,
  ): T | [] {
    if (offerResponse.result_match_filter) {
      return offerResponse.results
    }

    return []
  }
}
